import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { attendanceAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Upload, Download, FileText, FileSpreadsheet, CheckCircle, AlertTriangle, Loader2, Clock, Calendar, Users, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function AttendanceImportPage() {
  const { user: _user } = useAuth();
  const csvFileInputRef = useRef(null);
  const excelFileInputRef = useRef(null);
  
  // CSV Import State
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  const [csvImportHistory, setCsvImportHistory] = useState([]);
  const [loadingCsvHistory, setLoadingCsvHistory] = useState(true);
  const [dateMode, setDateMode] = useState('included');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [csvPreviewData, setCsvPreviewData] = useState(null);

  // Excel Import State
  const [excelFile, setExcelFile] = useState(null);
  const [excelUploading, setExcelUploading] = useState(false);
  const [excelPreviewing, setExcelPreviewing] = useState(false);
  const [excelPreviewData, setExcelPreviewData] = useState(null);
  const [excelImportResult, setExcelImportResult] = useState(null);
  const [excelImportHistory, setExcelImportHistory] = useState([]);
  const [loadingExcelHistory, setLoadingExcelHistory] = useState(true);
  const [autoDeductLeave, setAutoDeductLeave] = useState(true);

  useEffect(() => {
    fetchCsvImportHistory();
    fetchExcelImportHistory();
  }, []);

  const fetchCsvImportHistory = async () => {
    try {
      const res = await attendanceAPI.getImportHistory(20);
      setCsvImportHistory(res.data.batches || []);
    } catch (error) {
      console.error('Failed to fetch CSV import history:', error);
    } finally {
      setLoadingCsvHistory(false);
    }
  };

  const fetchExcelImportHistory = async () => {
    try {
      const res = await attendanceAPI.getExcelImportHistory(20);
      setExcelImportHistory(res.data.imports || []);
    } catch (error) {
      console.error('Failed to fetch Excel import history:', error);
    } finally {
      setLoadingExcelHistory(false);
    }
  };

  // CSV Import Functions
  const handleCsvFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').slice(0, 6);
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = lines.slice(1).map(line => line.split(',').map(c => c.trim()));
      setCsvPreviewData({ headers, rows, file });
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!csvPreviewData?.file) {
      toast.error('Please select a file first');
      return;
    }

    setCsvUploading(true);
    setCsvImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', csvPreviewData.file);
      formData.append('date_mode', dateMode);
      if (dateMode === 'separate') {
        formData.append('selected_date', selectedDate);
      }

      const res = await attendanceAPI.importCSV(formData);
      setCsvImportResult(res.data);

      if (res.data.status === 'completed') {
        toast.success(`Imported ${res.data.success_count} records successfully`);
      } else {
        toast.error('Import completed with errors');
      }

      fetchCsvImportHistory();
      setCsvPreviewData(null);
      if (csvFileInputRef.current) {
        csvFileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
      setCsvImportResult({
        status: 'failed',
        error_count: 1,
        errors: [error.response?.data?.detail || 'Unknown error']
      });
    } finally {
      setCsvUploading(false);
    }
  };

  const downloadCsvTemplate = () => {
    const headers = ['Employee id', 'Employee name', 'Punch In time', 'Punch Out time', 'Total hours in a day'];
    const sampleRows = [
      ['EMP001', 'John Doe', '09:30:00', '18:45:00', '9.25'],
      ['EMP002', 'Jane Smith', '09:15:00', '18:30:00', '9.25'],
    ];
    const csv = [headers.join(','), ...sampleRows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendance_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Excel Import Functions
  const handleExcelFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setExcelFile(file);
    setExcelPreviewData(null);
    setExcelImportResult(null);
  };

  const handleExcelPreview = async () => {
    if (!excelFile) {
      toast.error('Please select a file first');
      return;
    }

    setExcelPreviewing(true);
    setExcelPreviewData(null);

    try {
      const formData = new FormData();
      formData.append('file', excelFile);

      const res = await attendanceAPI.previewExcel(formData);
      setExcelPreviewData(res.data);
      toast.success(`Parsed ${res.data.total_records} records`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse file');
    } finally {
      setExcelPreviewing(false);
    }
  };

  const handleExcelImport = async () => {
    if (!excelFile) {
      toast.error('Please select a file first');
      return;
    }

    setExcelUploading(true);
    setExcelImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      formData.append('auto_deduct_leave', autoDeductLeave);

      const res = await attendanceAPI.importExcel(formData, autoDeductLeave);
      setExcelImportResult(res.data);

      if (res.data.success) {
        toast.success(`Imported ${res.data.saved_count} records successfully`);
        if (res.data.half_day_deductions > 0) {
          toast.info(`${res.data.half_day_deductions} half-day leave deductions processed`);
        }
      }

      fetchExcelImportHistory();
      setExcelFile(null);
      setExcelPreviewData(null);
      if (excelFileInputRef.current) {
        excelFileInputRef.current.value = '';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setExcelUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      full_day: 'bg-green-100 text-green-800',
      half_day: 'bg-yellow-100 text-yellow-800',
      short_day: 'bg-orange-100 text-orange-800',
      single_punch: 'bg-red-100 text-red-800',
      absent: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{status.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Upload className="h-6 w-6" />
            Attendance Import
          </h1>
          <p className="text-muted-foreground">Import biometric attendance data from CSV or Excel</p>
        </div>
      </div>

      <Tabs defaultValue="excel" className="space-y-4">
        <TabsList>
          <TabsTrigger value="excel" className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Excel Import (Biometric)
          </TabsTrigger>
          <TabsTrigger value="csv" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            CSV Import
          </TabsTrigger>
          <TabsTrigger value="history">Import History</TabsTrigger>
        </TabsList>

        {/* Excel Import Tab */}
        <TabsContent value="excel" className="space-y-4">
          {/* Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                Excel Format (Biometric System Export)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Expected Excel Format:</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Row 0: Title (e.g., "List of Logs")</li>
                    <li>• Row 1: Duration (e.g., "01/01/2026 ~ 21/01/2026")</li>
                    <li>• Row 2: Headers (No., Name, day numbers)</li>
                    <li>• Row 3: Day names (Th, Fr, Sa, etc.)</li>
                    <li>• Row 4+: Employee data with punch times</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Work Hour Calculation:</h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span><strong>Full Day:</strong> ≥ 8.5 hours</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <MinusCircle className="h-4 w-4 text-yellow-500" />
                      <span><strong>Half Day:</strong> 5 - 8.5 hours (auto leave deduction)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-500" />
                      <span>30-min lunch break auto-deducted</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-purple-500" />
                      <span>First punch = check-in, Last punch = check-out</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upload Biometric Excel File</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Options */}
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="autoDeduct"
                    checked={autoDeductLeave}
                    onCheckedChange={setAutoDeductLeave}
                  />
                  <Label htmlFor="autoDeduct" className="text-sm cursor-pointer">
                    Auto-deduct half-day leave for shortfall hours (send email notification)
                  </Label>
                </div>
              </div>

              {/* File Input */}
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  ref={excelFileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleExcelFileSelect}
                  className="hidden"
                  id="excel-upload"
                />
                <label htmlFor="excel-upload" className="cursor-pointer">
                  <FileSpreadsheet className="h-12 w-12 mx-auto text-green-600 mb-4" />
                  <p className="text-lg font-medium">Click to select Excel file</p>
                  <p className="text-sm text-muted-foreground">.xlsx or .xls files only</p>
                </label>
              </div>

              {/* Selected File */}
              {excelFile && !excelPreviewData && (
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="h-8 w-8 text-green-600" />
                    <div>
                      <p className="font-medium">{excelFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(excelFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => {
                      setExcelFile(null);
                      if (excelFileInputRef.current) excelFileInputRef.current.value = '';
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleExcelPreview} disabled={excelPreviewing}>
                      {excelPreviewing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Parsing...
                        </>
                      ) : (
                        'Preview Data'
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Preview Data */}
              {excelPreviewData && (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold">{excelPreviewData.total_records}</div>
                        <p className="text-sm text-muted-foreground">Total Records</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-green-600">{excelPreviewData.summary?.full_days || 0}</div>
                        <p className="text-sm text-muted-foreground">Full Days</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-yellow-600">{excelPreviewData.summary?.half_days || 0}</div>
                        <p className="text-sm text-muted-foreground">Half Days</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-orange-600">{excelPreviewData.summary?.short_days || 0}</div>
                        <p className="text-sm text-muted-foreground">Short Days</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-2xl font-bold text-red-600">{excelPreviewData.summary?.single_punch || 0}</div>
                        <p className="text-sm text-muted-foreground">Single Punch</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Date Range */}
                  <Alert>
                    <Calendar className="h-4 w-4" />
                    <AlertTitle>Date Range</AlertTitle>
                    <AlertDescription>
                      {excelPreviewData.date_range} ({excelPreviewData.days_in_file} days)
                    </AlertDescription>
                  </Alert>

                  {/* Unmatched Employees Warning */}
                  {excelPreviewData.unmatched_employees?.length > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Unmatched Employees ({excelPreviewData.unmatched_employees.length})</AlertTitle>
                      <AlertDescription>
                        <p>The following employees were not found in the system:</p>
                        <ul className="mt-2 list-disc list-inside">
                          {excelPreviewData.unmatched_employees.map((name, i) => (
                            <li key={i}>{name}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Records Preview */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Preview Records (First 20)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[400px]">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Employee</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Check-in</TableHead>
                              <TableHead>Check-out</TableHead>
                              <TableHead>Hours</TableHead>
                              <TableHead>Punches</TableHead>
                              <TableHead>Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {excelPreviewData.records?.slice(0, 20).map((record, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-medium">{record.employee_name}</TableCell>
                                <TableCell>{record.date}</TableCell>
                                <TableCell className="font-mono">{record.check_in || '-'}</TableCell>
                                <TableCell className="font-mono">{record.check_out || '-'}</TableCell>
                                <TableCell className="font-mono">{record.hours_worked}</TableCell>
                                <TableCell>{record.punch_count}</TableCell>
                                <TableCell>{getStatusBadge(record.status)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => {
                      setExcelPreviewData(null);
                      setExcelFile(null);
                      if (excelFileInputRef.current) excelFileInputRef.current.value = '';
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleExcelImport} disabled={excelUploading}>
                      {excelUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Import {excelPreviewData.total_records} Records
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Import Result */}
              {excelImportResult && (
                <Alert variant={excelImportResult.success ? 'default' : 'destructive'}>
                  {excelImportResult.success ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                  <AlertTitle>
                    {excelImportResult.success ? 'Import Completed' : 'Import Failed'}
                  </AlertTitle>
                  <AlertDescription>
                    <div className="mt-2 space-y-1">
                      <p>Records saved: {excelImportResult.saved_count || 0}</p>
                      <p>Records skipped (duplicates): {excelImportResult.skipped_count || 0}</p>
                      {excelImportResult.half_day_deductions > 0 && (
                        <p className="text-yellow-600">Half-day deductions: {excelImportResult.half_day_deductions}</p>
                      )}
                      {excelImportResult.unmatched_employees?.length > 0 && (
                        <p className="text-orange-600">Unmatched employees: {excelImportResult.unmatched_employees.length}</p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CSV Import Tab */}
        <TabsContent value="csv" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">CSV Format Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Required Columns:</h4>
                  <ul className="space-y-1 text-sm">
                    <li className="flex items-center gap-2">
                      <Badge variant="outline">Employee id</Badge>
                      Must match employee_id in system
                    </li>
                    <li className="flex items-center gap-2">
                      <Badge variant="outline">Employee name</Badge>
                      For validation/display
                    </li>
                    <li className="flex items-center gap-2">
                      <Badge variant="outline">Punch In time</Badge>
                      Format: HH:MM:SS
                    </li>
                    <li className="flex items-center gap-2">
                      <Badge variant="outline">Punch Out time</Badge>
                      Format: HH:MM:SS
                    </li>
                    <li className="flex items-center gap-2">
                      <Badge variant="outline">Total hours</Badge>
                      Decimal (e.g., 9.25)
                    </li>
                  </ul>
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={downloadCsvTemplate}>
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV Template
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upload CSV</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Mode Selection */}
              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <Label>Date handling:</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dateMode"
                      value="included"
                      checked={dateMode === 'included'}
                      onChange={() => setDateMode('included')}
                    />
                    <span className="text-sm">Date in time columns</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="dateMode"
                      value="separate"
                      checked={dateMode === 'separate'}
                      onChange={() => setDateMode('separate')}
                    />
                    <span className="text-sm">Select date</span>
                  </label>
                </div>
                {dateMode === 'separate' && (
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-40"
                  />
                )}
              </div>

              {/* File Input */}
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  ref={csvFileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">Click to select CSV file</p>
                </label>
              </div>

              {/* Preview */}
              {csvPreviewData && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Preview: {csvPreviewData.file.name}</h4>
                    <Badge>{csvPreviewData.rows.length} rows</Badge>
                  </div>
                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {csvPreviewData.headers.map((h, i) => (
                            <TableHead key={i}>{h}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {csvPreviewData.rows.slice(0, 5).map((row, i) => (
                          <TableRow key={i}>
                            {row.map((cell, j) => (
                              <TableCell key={j} className="font-mono text-sm">{cell}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => {
                      setCsvPreviewData(null);
                      if (csvFileInputRef.current) csvFileInputRef.current.value = '';
                    }}>
                      Cancel
                    </Button>
                    <Button onClick={handleCsvUpload} disabled={csvUploading}>
                      {csvUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Import
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Import Result */}
              {csvImportResult && (
                <Alert variant={csvImportResult.status === 'completed' ? 'default' : 'destructive'}>
                  {csvImportResult.status === 'completed' ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                  <AlertTitle>
                    {csvImportResult.status === 'completed' ? 'Import Completed' : 'Import Failed'}
                  </AlertTitle>
                  <AlertDescription>
                    <div className="mt-2 space-y-1">
                      <p>Processed: {csvImportResult.record_count || 0}</p>
                      <p>Imported: {csvImportResult.success_count || 0}</p>
                      {csvImportResult.error_count > 0 && (
                        <p className="text-red-600">Errors: {csvImportResult.error_count}</p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          {/* Excel Import History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                Excel Import History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingExcelHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : excelImportHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No Excel import history
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File</TableHead>
                      <TableHead>Date Range</TableHead>
                      <TableHead>Imported By</TableHead>
                      <TableHead>Records</TableHead>
                      <TableHead>Saved</TableHead>
                      <TableHead>Half-Days</TableHead>
                      <TableHead>Unmatched</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {excelImportHistory.map(imp => (
                      <TableRow key={imp.id}>
                        <TableCell className="font-mono text-sm">{imp.filename}</TableCell>
                        <TableCell>{imp.date_range}</TableCell>
                        <TableCell>{imp.imported_by_name}</TableCell>
                        <TableCell>{imp.total_records}</TableCell>
                        <TableCell className="text-green-600">{imp.saved_count}</TableCell>
                        <TableCell className="text-yellow-600">{imp.half_day_count}</TableCell>
                        <TableCell className="text-orange-600">{imp.unmatched_employees?.length || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* CSV Import History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                CSV Import History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingCsvHistory ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : csvImportHistory.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No CSV import history
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File</TableHead>
                      <TableHead>Imported By</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Records</TableHead>
                      <TableHead>Success</TableHead>
                      <TableHead>Errors</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {csvImportHistory.map(batch => (
                      <TableRow key={batch.id}>
                        <TableCell className="font-mono text-sm">{batch.file_name}</TableCell>
                        <TableCell>{batch.imported_by_name}</TableCell>
                        <TableCell>{new Date(batch.imported_at).toLocaleString()}</TableCell>
                        <TableCell>{batch.record_count}</TableCell>
                        <TableCell className="text-green-600">{batch.success_count}</TableCell>
                        <TableCell className={batch.error_count > 0 ? 'text-red-600' : ''}>
                          {batch.error_count}
                        </TableCell>
                        <TableCell>
                          <Badge variant={batch.status === 'completed' ? 'default' : 'destructive'}>
                            {batch.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
